import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Tooltip,
  Alert,
  CardHeader,
  CardContent,
  CardActions,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Save, Refresh, CheckCircle, Cancel, HelpOutline } from "@mui/icons-material";
import { DocumentDto } from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import {
  useOcrFieldMapping,
  OcrFieldData,
  RawOcrExtraction,
} from "../../../stores/Documents/documents.queries";
import {
  useSaveRxFromExtraction,
  useExtractDocumentOcr,
} from "../../../stores/Documents/documents.mutations";
import RxSavePreflightDialog, { PreflightResult } from "./RxSavePreflightDialog";
import { useExtractionMatching } from "./useExtractionMatching";
import {
  PatientAddressIndicator,
  PatientIdentityIndicator,
  DoctorIndicator,
  ClinicIndicator,
} from "./ExtractionMatchIndicators";
import PatientAddressMatchDialog from "./PatientAddressMatchDialog";
import DoctorClinicMatchDialog from "./DoctorClinicMatchDialog";
import { TherapyType } from "../../../api-client";
import { inferRxType } from "./inferRxType";



// ============================================================================
// Local Form Data Type (for user editing)
// ============================================================================

/**
 * Local form data type for editing extraction values.
 * Uses primitive types (string/number/boolean) for form fields.
 */
interface ExtractionFormData {
  // Insurance / Payer
  payerName?: string | null;
  payerIk?: string | null;
  insureeNumber?: string | null;
  insuranceStatus?: string | null;

  // Patient (for display/matching)
  patientName?: string | null;
  patientAddress?: string | null;
  patientDateOfBirth?: string | null;

  // Doctor / Clinic
  doctorNumber?: string | null;
  doctorName?: string | null;
  doctorSpecialization?: string | null;
  practiceNumber?: string | null;
  practiceAddress?: string | null;
  practiceContact?: string | null;

  // Prescription core
  date?: string | null;
  diagnosisCodes?: string | null;
  diagnosisText?: string | null;
  diagnosisGroupCode?: string | null;
  therapyType?: string | null;
  mainSymptom?: string | null;
  symptoms?: string | null;

  // Treatment
  prescribedRemedy?: string | null;
  treatmentUnitCount?: number | null;
  therapyFrequency?: string | null;

  // Flags
  therapyReport?: boolean | null;
  isHomeVisit?: boolean | null;
  urgent?: boolean | null;
  isSignedByDoctor?: boolean | null;

  // Extra
  additionalNotes?: string | null;
  serviceProviderId?: string | null;

  // Inferred (not from OCR fields directly)
  rxType?: string | null;
}

// ============================================================================
// Confidence threshold configuration
// ============================================================================

const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.5,
  MEDIUM: 0.3,
};

type ConfidenceLevel = "high" | "medium" | "low" | "unknown";

/**
 * Get confidence level based on value
 */
const getConfidenceLevel = (confidence: number | undefined): ConfidenceLevel => {
  if (confidence === undefined) return "unknown";
  if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return "high";
  if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return "medium";
  return "low";
};

/**
 * Format a date string to dd.mm.yyyy format
 */
const formatDateForDisplay = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  
  // Try to parse ISO format (yyyy-mm-dd) or existing dd.mm.yyyy
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
  }
  
  // Already in dd.mm.yyyy format or similar
  const ddmmMatch = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (ddmmMatch) {
    return dateStr;
  }
  
  // Return as-is if no pattern matches
  return dateStr;
};

/**
 * Get styling for confidence level - returns sx props for TextField
 */
const getConfidenceStyle = (level: ConfidenceLevel): Record<string, any> | undefined => {
  switch (level) {
    case "low":
      return {
        "& .MuiOutlinedInput-root": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#f44336",
            borderWidth: 2,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d32f2f",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d32f2f",
          },
        },
      };
    case "medium":
      return {
        "& .MuiOutlinedInput-root": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ff9800",
            borderWidth: 2,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#f57c00",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#f57c00",
          },
        },
      };
    default:
      return undefined;
  }
};

/**
 * Get tooltip text for confidence level
 */
const getConfidenceTooltip = (level: ConfidenceLevel, confidence?: number): string => {
  const percent = confidence !== undefined ? `${Math.round(confidence * 100)}%` : "N/A";
  switch (level) {
    case "low":
      return `Niedrige Konfidenz (${percent}) - Bitte überprüfen`;
    case "medium":
      return `Mittlere Konfidenz (${percent}) - Möglicherweise korrigieren`;
    case "high":
      return `Hohe Konfidenz (${percent})`;
    default:
      return "Konfidenz unbekannt";
  }
};

// ============================================================================
// Form Field Configuration
// ============================================================================

interface FormFieldConfig {
  key: keyof ExtractionFormData;
  label: string;
  type: "text" | "number" | "checkbox" | "date" | "select";
  multiline?: boolean;
  rows?: number;
  options?: { value: string; label: string }[];
}

interface FormSection {
  title: string;
  fields: FormFieldConfig[];
}

const FORM_SECTIONS: FormSection[] = [
  {
    title: "Versicherung / Kostenträger",
    fields: [
      { key: "payerName", label: "Kostenträger", type: "text" },
      { key: "payerIk", label: "IK-Nummer", type: "text" },
      { key: "insureeNumber", label: "Versichertennummer", type: "text" },
      { key: "insuranceStatus", label: "Status", type: "text" },
    ],
  },
  {
    title: "Patient",
    fields: [
      { key: "patientName", label: "Name", type: "text" },
      { key: "patientDateOfBirth", label: "Geburtsdatum", type: "date" },
    ],
  },
  {
    title: "Adresse",
    fields: [
      { key: "patientAddress", label: "Adresse", type: "text", multiline: true, rows: 2 },
    ],
  },
  {
    title: "Arzt / Praxis",
    fields: [
      { key: "doctorNumber", label: "Arzt-Nr. (LANR)", type: "text" },
      { key: "doctorName", label: "Arztname", type: "text" },
      { key: "doctorSpecialization", label: "Fachrichtung", type: "text" },
      { key: "practiceNumber", label: "BSNR", type: "text" },
      { key: "practiceAddress", label: "Praxisadresse", type: "text", multiline: true, rows: 2 },
      { key: "practiceContact", label: "Kontakt", type: "text" },
    ],
  },
  {
    title: "Verordnung",
    fields: [
      { key: "date", label: "Datum", type: "date" },
      { key: "diagnosisCodes", label: "ICD-10 Codes", type: "text" },
      { key: "diagnosisText", label: "Diagnose", type: "text", multiline: true, rows: 2 },
      { key: "diagnosisGroupCode", label: "Diagnosegruppe", type: "text" },
      {
        key: "therapyType",
        label: "Therapieart",
        type: "select",
        options: [
          { value: TherapyType.Physiotherapie, label: "Physiotherapie" },
          { value: TherapyType.Ergotherapie, label: "Ergotherapie" },
        ],
      },
      { key: "mainSymptom", label: "Leitsymptomatik (a/b/c/d)", type: "text" },
      { key: "symptoms", label: "Symptome", type: "text", multiline: true, rows: 2 },
    ],
  },
  {
    title: "Behandlung",
    fields: [
      { key: "prescribedRemedy", label: "Heilmittel", type: "text" },
      { key: "treatmentUnitCount", label: "Behandlungseinheiten", type: "number" },
      { key: "therapyFrequency", label: "Therapiefrequenz", type: "text" },
    ],
  },
  {
    title: "Optionen",
    fields: [
      { key: "therapyReport", label: "Therapiebericht", type: "checkbox" },
      { key: "isHomeVisit", label: "Hausbesuch", type: "checkbox" },
      { key: "urgent", label: "Dringend", type: "checkbox" },
    ],
  },
  {
    title: "Zusätzliche Angaben",
    fields: [
      { key: "additionalNotes", label: "Anmerkungen", type: "text", multiline: true, rows: 3 },
      { key: "serviceProviderId", label: "Leistungserbringer-IK", type: "text" },
    ],
  },
];

// ============================================================================
// Component Props
// ============================================================================

interface RxExtractionFormProps {
  document: DocumentDto;
  patientId: number | null;
  onSaveSuccess?: (rxId: string, isNew: boolean) => void;
  onPatientResolved?: (patientId: number) => void;
  onOpenPatientSearch?: (name: string | null, dob: string | null, insuranceNo: string | null) => void;
}

// ============================================================================
// Main Component
// ============================================================================

const RxExtractionForm: React.FC<RxExtractionFormProps> = ({
  document,
  patientId,
  onSaveSuccess,
  onPatientResolved,
  onOpenPatientSearch,
}) => {
  // API access
  const { documents: documentsApi } = useApi();

  // Queries and mutations
  const { data: fieldMapping, error: fieldMappingError } = useOcrFieldMapping();
  const saveRxMutation = useSaveRxFromExtraction();
  const extractMutation = useExtractDocumentOcr();

  // Form state
  const [formData, setFormData] = useState<ExtractionFormData>({});
  const [initialFormData, setInitialFormData] = useState<ExtractionFormData>({});
  const [rxId, setRxId] = useState<string | null>(null);
  const [signatureStatus, setSignatureStatus] = useState<string>("");
  const [signatureWasOcrPrefilled, setSignatureWasOcrPrefilled] = useState<boolean>(false);

  // Confidence data
  const [rawOcrFields, setRawOcrFields] = useState<Record<string, OcrFieldData>>({});

  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preflightOpen, setPreflightOpen] = useState(false);

  // ── Extraction matching (address, doctor, clinic) ──────────
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [doctorClinicDialogOpen, setDoctorClinicDialogOpen] = useState(false);

  const matchResult = useExtractionMatching({
    patientId: patientId ?? undefined,
    extractedAddress: formData.patientAddress,
    extractedPatientName: formData.patientName,
    extractedPatientDob: formData.patientDateOfBirth,
    extractedInsuranceNo: formData.insureeNumber,
    doctorNumber: formData.doctorNumber,
    doctorName: formData.doctorName,
    practiceNumber: formData.practiceNumber,
    practiceAddress: formData.practiceAddress,
    practiceContact: formData.practiceContact,
    doctorSpecialization: formData.doctorSpecialization,
  });

  // Check if form has changes
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
  const isUpdate = !!rxId;
  const saving = saveRxMutation.isPending;
  const extractionLoading = extractMutation.isPending;

  // Load extraction data when document changes
  useEffect(() => {
    loadExtractionData();
  }, [document.id]);

  /**
   * Load RxExtraction and raw OCR data via useApi()
   */
  const loadExtractionData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load data in parallel using useApi()
      const [rxExtraction, rawOcr] = await Promise.all([
        documentsApi.getDocumentRxExtraction(document.id).catch(() => null),
        documentsApi.getDocumentOcr(document.id).catch(() => null),
      ]);

      // Extract fields from raw OCR:
      // - getDocumentOcr returns { documentId, analyzerId, extractedAt, extraction: <raw Azure payload> }
      // - document.extraction IS the raw Azure payload directly
      // Raw Azure payload has: { contents: [{ fields: { FieldName: { confidence, valueString, ... } } }] }
      const ocrResponse = (rawOcr || document.extraction) as any;
      const ocrData = (ocrResponse?.extraction ?? ocrResponse) as RawOcrExtraction | null;
      const fields = ocrData?.contents?.[0]?.fields ?? (ocrData as any)?.fields;

      if (fields) {
        setRawOcrFields(fields as Record<string, OcrFieldData>);
      } else {
        setRawOcrFields({});
      }

      // Infer rxType from the full OCR text (markdown)
      const ocrMarkdown = ocrData?.contents?.[0]?.markdown as string | undefined;
      const inferredRxType = inferRxType(ocrMarkdown ?? "");

      // Set form data - extract primitive values from the API response
      const extraction = rxExtraction as ExtractionFormData | null;
      if (extraction) {
        const withRxType = { ...extraction, rxType: extraction.rxType ?? inferredRxType };
        setFormData(withRxType);
        setInitialFormData(withRxType);
        // Check if document has associated rxId (would come from backend)
        setRxId((extraction as any).rxId || null);

        // Pre-fill signature status from OCR detection — user must still confirm
        if (extraction.isSignedByDoctor === true) {
          setSignatureStatus("vorhanden");
          setSignatureWasOcrPrefilled(true);
        } else if (extraction.isSignedByDoctor === false) {
          setSignatureStatus("fehlend");
          setSignatureWasOcrPrefilled(true);
        } else {
          setSignatureStatus("");
          setSignatureWasOcrPrefilled(false);
        }
      } else {
        setFormData({});
        setInitialFormData({});
        setRxId(null);
        setSignatureStatus("");
        setSignatureWasOcrPrefilled(false);
      }
    } catch (err) {
      console.error("Error loading extraction data:", err);
      setError("Fehler beim Laden der Extraktionsdaten");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get confidence for a form field
   */
  const getFieldConfidence = useCallback(
    (fieldKey: keyof ExtractionFormData): number | undefined => {
      if (!fieldMapping) return undefined;
      const ocrFieldName = fieldMapping[fieldKey as string];
      if (!ocrFieldName) return undefined;

      const ocrField = rawOcrFields[ocrFieldName];
      return ocrField?.confidence;
    },
    [fieldMapping, rawOcrFields]
  );

  /**
   * Handle text/number field change
   */
  const handleFieldChange = (fieldKey: keyof ExtractionFormData, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  /**
   * Handle checkbox change
   */
  const handleCheckboxChange = (fieldKey: keyof ExtractionFormData, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: checked,
    }));
  };

  /**
   * Handle save button click.
   * For new Rxs → open preflight dialog (account selection + duplicate check).
   * For updates → save directly (account already assigned).
   */
  const handleSaveClick = () => {
    if (isUpdate) {
      handleSubmit(null, signatureStatus);
    } else {
      setPreflightOpen(true);
    }
  };

  /**
   * Handle preflight dialog confirmation.
   */
  const handlePreflightConfirm = (result: PreflightResult) => {
    setPreflightOpen(false);
    if (result.existingRxId) {
      setRxId(result.existingRxId);
    }
    // Apply rxType selection from preflight into extraction data
    setFormData((prev) => ({
      ...prev,
      rxType: result.rxType,
    }));
    handleSubmit(result.accountId, signatureStatus, result.existingRxId, result.rxType, result.unvirtualize);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (
    accountId: string | null,
    signatureStatus?: string | null,
    overrideRxId?: string | null,
    overrideRxType?: string | null,
    unvirtualize?: boolean
  ) => {
    if (!patientId) return;
    setError(null);
    const effectiveRxId = overrideRxId !== undefined ? overrideRxId : rxId;

    // Convert form data to the API expected format
    const extractionData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [key, value])
    ) as any;

    // Inject resolved doctor/clinic entity IDs from the matching hook so
    // the backend can link the Rx to the correct FK entities, not just
    // store the LANR/BSNR strings.
    if (matchResult.doctor.existingDoctor?.id) {
      extractionData.doctorId = matchResult.doctor.existingDoctor.id;
    }
    if (matchResult.clinic.existingClinic?.id) {
      extractionData.clinicId = matchResult.clinic.existingClinic.id;
    }

    // Override rxType if preflight selected one
    if (overrideRxType) {
      extractionData.rxType = overrideRxType;
    }

    saveRxMutation.mutate(
      {
        documentId: document.id,
        patientId,
        data: {
          rxId: effectiveRxId as any,
          accountId: (accountId || undefined) as any,
          signatureStatus: (signatureStatus ?? undefined) as any,
          unvirtualize: unvirtualize || undefined,
          extraction: extractionData,
        } as any,
      },
      {
        onSuccess: (result) => {
          // SaveRxResponse has { rx: RxDto, therapies, validRxSeriesIds }
          const savedRxId = result?.[0]?.rx?.id;
          const isNew = !effectiveRxId;

          // Update rxId if this was a create
          if (isNew && savedRxId) {
            setRxId(savedRxId);
          }

          // Update initial data to reflect saved state
          setInitialFormData(formData);

          onSaveSuccess?.(savedRxId || effectiveRxId || "", isNew);
        },
        onError: (err) => {
          console.error("Error saving Rx:", err);
          setError("Fehler beim Speichern des Rezepts");
        },
      }
    );
  };

  /**
   * Handle extract/re-extract
   */
  const handleExtract = async () => {
    extractMutation.mutate(document.id, {
      onSuccess: () => {
        // Reload data after extraction
        loadExtractionData();
      },
    });
  };

  /**
   * Render a form field with confidence highlighting
   */
  const renderField = (config: FormFieldConfig) => {
    const confidence = getFieldConfidence(config.key);
    const confidenceLevel = getConfidenceLevel(confidence);
    const confidenceStyle = getConfidenceStyle(confidenceLevel);
    const tooltip = getConfidenceTooltip(confidenceLevel, confidence);

    const value = formData[config.key];

    if (config.type === "checkbox") {
      return (
        <Tooltip title={tooltip} arrow placement="right">
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => handleCheckboxChange(config.key, e.target.checked)}
                sx={
                  confidenceLevel === "low"
                    ? { color: "#f44336" }
                    : confidenceLevel === "medium"
                    ? { color: "#ff9800" }
                    : {}
                }
              />
            }
            label={
              <Typography
                sx={
                  confidenceLevel === "low"
                    ? { color: "#f44336", fontWeight: 500 }
                    : confidenceLevel === "medium"
                    ? { color: "#ff9800", fontWeight: 500 }
                    : {}
                }
              >
                {config.label}
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Tooltip>
      );
    }

    if (config.type === "select" && config.options) {
      return (
        <Tooltip title={tooltip} arrow placement="right">
          <TextField
            fullWidth
            size="small"
            select
            label={config.label}
            value={value ?? ""}
            onChange={(e) => handleFieldChange(config.key, e.target.value || null)}
            sx={confidenceStyle}
            InputLabelProps={{
              sx:
                confidenceLevel === "low"
                  ? { color: "#f44336", fontWeight: 600 }
                  : confidenceLevel === "medium"
                  ? { color: "#ff9800", fontWeight: 600 }
                  : {},
            }}
          >
            <MenuItem value="">
              <em>Nicht angegeben</em>
            </MenuItem>
            {config.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={tooltip} arrow placement="right">
        <TextField
          fullWidth
          size="small"
          label={config.label}
          type={config.type === "number" ? "number" : "text"}
          multiline={config.multiline}
          rows={config.rows}
          value={config.type === "date" ? formatDateForDisplay(value as string) : (value ?? "")}
          onChange={(e) => {
            const newValue =
              config.type === "number"
                ? e.target.value === ""
                  ? null
                  : Number(e.target.value)
                : e.target.value || null;
            handleFieldChange(config.key, newValue);
          }}
          sx={confidenceStyle}
          InputLabelProps={{
            sx:
              confidenceLevel === "low"
                ? { color: "#f44336", fontWeight: 600 }
                : confidenceLevel === "medium"
                ? { color: "#ff9800", fontWeight: 600 }
                : {},
          }}
        />
      </Tooltip>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <CardContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
        <CircularProgress size={32} />
      </CardContent>
    );
  }

  const hasExtraction = document.extraction || Object.keys(formData).length > 0;

  return (
    <>
      <CardHeader
        title="Rezeptdaten"
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        action={
          <Button
            size="small"
            onClick={handleExtract}
            disabled={extractionLoading}
            startIcon={extractionLoading ? <CircularProgress size={16} /> : <Refresh />}
          >
            {hasExtraction ? "Erneut extrahieren" : "Extrahieren"}
          </Button>
        }
        sx={{ pb: 0 }}
      />

      <CardContent sx={{ flex: 1, overflow: "auto", pt: 1 }}>
        {/* Error Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {fieldMappingError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Konfidenz-Daten nicht verfügbar: Feldzuordnung konnte nicht geladen werden
          </Alert>
        )}

        {!hasExtraction ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Keine Extraktionsdaten vorhanden.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Klicken Sie auf "Extrahieren", um OCR-Daten aus diesem Dokument zu gewinnen.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Extraction timestamp - click to copy raw extraction JSON */}
            {document.extractedAt && (
              <Tooltip title="Klicken, um Rohdaten in die Zwischenablage zu kopieren" arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 2, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => {
                    const data = document.extraction ?? rawOcrFields;
                    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                  }}
                >
                  Extrahiert am: {new Date(document.extractedAt).toLocaleString("de-DE")}
                </Typography>
              </Tooltip>
            )}

            {/* Form Sections */}
            {FORM_SECTIONS.map((section, sectionIndex) => (
              <Box key={section.title} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="primary"
                  >
                    {section.title}
                  </Typography>

                  {/* Match indicators next to section titles */}
                  {section.title === "Patient" && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <PatientIdentityIndicator
                        match={matchResult.patientIdentity}
                        onOpenSearch={() =>
                          onOpenPatientSearch?.(
                            formData.patientName ?? null,
                            formData.patientDateOfBirth ?? null,
                            formData.insureeNumber ?? null
                          )
                        }
                        onAutoAssign={!patientId ? onPatientResolved : undefined}
                      />
                    </Box>
                  )}
                  {section.title === "Adresse" && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <PatientAddressIndicator
                        match={matchResult.address}
                        onOpenDialog={() => setAddressDialogOpen(true)}
                      />
                    </Box>
                  )}
                  {section.title === "Arzt / Praxis" && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <DoctorIndicator
                        match={matchResult.doctor}
                        onOpenDialog={() => setDoctorClinicDialogOpen(true)}
                      />
                      <ClinicIndicator
                        match={matchResult.clinic}
                        onOpenDialog={() => setDoctorClinicDialogOpen(true)}
                      />
                    </Box>
                  )}
                </Box>

                {/* Checkbox fields in a row */}
                {section.fields[0]?.type === "checkbox" ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {section.fields.map((field) => (
                      <Box key={field.key} sx={{ minWidth: "140px" }}>
                        {renderField(field)}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  // Regular fields stacked
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {section.fields.map((field) => (
                      <Box key={field.key}>{renderField(field)}</Box>
                    ))}
                  </Box>
                )}

                {sectionIndex < FORM_SECTIONS.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            ))}

            <Divider sx={{ mt: 3, mb: 2 }} />

            {/* ── Signature Status (required final decision) ───────── */}
            <Box sx={{ p: 0, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 0.5 }}>
                Arztunterschrift
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Bitte prüfen Sie das Originalrezept und bestätigen Sie den Status der Arztunterschrift.
                {signatureWasOcrPrefilled && " Vorauswahl basiert auf der OCR-Erkennung."}
              </Typography>
              <ToggleButtonGroup
                value={signatureStatus}
                exclusive
                onChange={(_, val) => {
                  if (val !== null) setSignatureStatus(val);
                }}
                size="small"
                fullWidth
                color={signatureStatus === "fehlend" ? "error" : "primary"}
              >
                <ToggleButton value="vorhanden" sx={{ textTransform: "none", gap: 0.5 }}>
                  <CheckCircle fontSize="small" /> vorhanden
                </ToggleButton>
                <ToggleButton value="fehlend" sx={{ textTransform: "none", gap: 0.5 }}>
                  <Cancel fontSize="small" /> fehlend
                </ToggleButton>
                <ToggleButton value="nicht kontrolliert" sx={{ textTransform: "none", gap: 0.5 }}>
                  <HelpOutline fontSize="small" /> nicht kontrolliert
                </ToggleButton>
              </ToggleButtonGroup>
              {!signatureStatus && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Pflichtangabe — bitte auswählen, um das Rezept zu speichern.
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>

      {/* Footer with Save Button */}
      {hasExtraction && (
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Box sx={{ width: "100%" }}>
            <Button
              fullWidth
              variant="contained"
              color={isUpdate ? "primary" : "success"}
              onClick={handleSaveClick}
              disabled={saving || !patientId || !signatureStatus || (isUpdate && !hasChanges)}
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            >
              {saving
                ? "Wird gespeichert..."
                : !patientId
                ? "Patient zuordnen um zu speichern"
                : !signatureStatus
                ? "Arztunterschrift bestätigen um zu speichern"
                : isUpdate
                ? "Rezept aktualisieren"
                : "Neues Rezept erstellen"}
            </Button>
            {isUpdate && !hasChanges && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                Keine Änderungen vorhanden
              </Typography>
            )}
          </Box>
        </CardActions>
      )}

      {/* Pre-save preflight dialog */}
      {patientId && (
        <RxSavePreflightDialog
          open={preflightOpen}
          onClose={() => setPreflightOpen(false)}
          onConfirm={handlePreflightConfirm}
          patientId={patientId}
          extractionDate={formData.date}
          extractionTherapy={formData.prescribedRemedy}
          extractionAmount={formData.treatmentUnitCount}
          inferredRxType={formData.rxType}
          saving={saving}
        />
      )}

      {/* Patient address match dialog */}
      {patientId && (
        <PatientAddressMatchDialog
          open={addressDialogOpen}
          onClose={() => setAddressDialogOpen(false)}
          match={matchResult.address}
          patientId={patientId}
        />
      )}

      {/* Doctor / Clinic match dialog */}
      <DoctorClinicMatchDialog
        open={doctorClinicDialogOpen}
        onClose={() => setDoctorClinicDialogOpen(false)}
        doctorMatch={matchResult.doctor}
        clinicMatch={matchResult.clinic}
      />
    </>
  );
};

export default RxExtractionForm;
